import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { NavLink, Route, Routes } from 'react-router';

import type { StatusResponse } from '../../../shared/api.js';
import { api } from '../lib/api';
import { AuditSection } from './Audit';
import { ConnectionsSection } from './Connections';
import { EmailSection } from './EmailSettings';
import { ScheduleSection } from './Schedule';
import { SystemSection } from './System';
import { TelegramSection } from './TelegramAccounts';
import { UsersSection } from './Users';
import { WhatsAppSection } from './WhatsAppAccounts';

const MIN_PASSWORD = 12;

/** The signed-in user's own password. Other sessions and Claude connectors are signed out. */
function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const change = useMutation({
    mutationFn: () => api.changePassword({ current, next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setRepeat('');
    },
  });
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD && next === repeat;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) change.mutate();
  };

  return (
    <form className="card form" onSubmit={submit}>
      <div className="card__header">
        <h3>Пароль</h3>
      </div>
      <label>
        Текущий пароль
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
      </label>
      <label>
        Новый пароль <span className="muted small">(от {MIN_PASSWORD} символов)</span>
        <input
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
        />
      </label>
      <label>
        Новый пароль ещё раз
        <input
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          required
        />
      </label>
      {mismatch && <p className="text-error small">Пароли не совпадают.</p>}
      {change.isError && <p className="text-error small">{change.error.message}</p>}
      {change.isSuccess && (
        <p className="small">
          Пароль изменён. Другие входы и коннекторы в приложениях Claude нужно подключить заново.
        </p>
      )}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={!ready || change.isPending}>
          {change.isPending ? 'Меняю…' : 'Сменить пароль'}
        </button>
      </div>
    </form>
  );
}

function AccountSection({ signedIn }: { signedIn: boolean }) {
  return (
    <section>
      <h2>Учётная запись</h2>
      {signedIn ? (
        <PasswordCard />
      ) : (
        <p className="muted">
          Здесь вы работаете как владелец на этой машине; пароли учёток меняются командой{' '}
          <code>pnpm users passwd &lt;login&gt;</code>.
        </p>
      )}
    </section>
  );
}

/** Settings with its own menu: account, connections, users, Telegram, WhatsApp, schedule and the journal (owner), system. */
export function Settings({
  signedIn,
  isOwner,
  status,
}: {
  signedIn: boolean;
  isOwner: boolean;
  status: StatusResponse;
}) {
  return (
    <div className="page settings">
      <nav className="subnav" aria-label="Разделы настроек">
        <NavLink to="/settings" end>
          Учётная запись
        </NavLink>
        <NavLink to="/settings/connections">Подключения</NavLink>
        {isOwner && <NavLink to="/settings/users">Пользователи</NavLink>}
        {isOwner && <NavLink to="/settings/telegram">Telegram</NavLink>}
        {isOwner && <NavLink to="/settings/whatsapp">WhatsApp</NavLink>}
        {isOwner && <NavLink to="/settings/email">Почта</NavLink>}
        {isOwner && <NavLink to="/settings/schedule">Расписание</NavLink>}
        {isOwner && <NavLink to="/settings/journal">Журнал</NavLink>}
        <NavLink to="/settings/system">Система</NavLink>
      </nav>
      <div className="settings__body">
        <Routes>
          <Route index element={<AccountSection signedIn={signedIn} />} />
          <Route path="connections" element={<ConnectionsSection />} />
          {isOwner && <Route path="users" element={<UsersSection />} />}
          {isOwner && <Route path="telegram" element={<TelegramSection />} />}
          {isOwner && <Route path="whatsapp" element={<WhatsAppSection />} />}
          {isOwner && <Route path="email" element={<EmailSection />} />}
          {isOwner && <Route path="schedule" element={<ScheduleSection />} />}
          {isOwner && <Route path="journal" element={<AuditSection />} />}
          <Route path="system" element={<SystemSection status={status} />} />
          <Route path="*" element={<p className="muted">Раздел не найден.</p>} />
        </Routes>
      </div>
    </div>
  );
}
